import React, { useState } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import { Box } from "@material-ui/core";

const useStyles = makeStyles(() => ({
  root: {
    position: "relative",
    width: "100%",
  },
  trigger: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
    border: "1px solid #E1E6F1",
    borderRadius: "8px",
    padding: "13px 14px",
    background: "#fff",
    cursor: "pointer",
    textAlign: "left",
    transition: "border-color .2s",
    "&:hover": { borderColor: "#54606F" },
    "&:disabled": { cursor: "not-allowed", opacity: 0.5 },
  },
  triggerLabel: {
    font: "500 14px/17px inherit",
    color: "#1A1E24",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  triggerPlaceholder: {
    font: "500 14px/17px inherit",
    color: "#A1ADBA",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  chevron: { flex: "none" },
  overlay: {
    position: "fixed",
    inset: 0,
    zIndex: 30,
  },
  menu: {
    position: "absolute",
    top: "calc(100% + 6px)",
    left: 0,
    right: 0,
    background: "#fff",
    border: "1px solid #E1E6F1",
    borderRadius: "10px",
    boxShadow: "0 12px 32px rgba(16,24,40,.12)",
    zIndex: 40,
    overflow: "hidden",
  },
  searchWrap: {
    padding: "10px",
    borderBottom: "1px solid #E1E6F1",
    position: "relative",
  },
  searchIcon: {
    position: "absolute",
    left: "22px",
    top: "50%",
    transform: "translateY(-50%)",
  },
  search: {
    width: "100%",
    border: "1px solid #E1E6F1",
    borderRadius: "8px",
    padding: "10px 12px 10px 36px",
    font: "500 13px/16px inherit",
    color: "#1A1E24",
    outline: "none",
    transition: "border-color .2s",
    "&:hover": { borderColor: "#54606F" },
    "&:focus": { borderColor: "#54606F" },
  },
  list: {
    maxHeight: "300px",
    overflow: "auto",
    padding: "6px",
  },
  option: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "10px",
    border: "none",
    background: "transparent",
    borderRadius: "8px",
    cursor: "pointer",
    textAlign: "left",
    "&:hover": { background: "#F3F5F7" },
  },
  optionText: { flex: 1, minWidth: 0 },
  optionPrimary: {
    font: "500 14px/17px inherit",
    color: "#1A1E24",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  optionSecondary: {
    font: "500 12px/15px inherit",
    color: "#A1ADBA",
    marginTop: "2px",
  },
  empty: {
    textAlign: "center",
    color: "#A1ADBA",
    padding: "22px",
    font: "500 13px/16px inherit",
  },
}));

const Chevron = ({ className }) => (
  <svg className={className} width="18" height="18" viewBox="0 0 24 24" fill="none"
    stroke="#A1ADBA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const SearchIcon = ({ className }) => (
  <svg className={className} width="16" height="16" viewBox="0 0 24 24" fill="none"
    stroke="#A1ADBA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

/**
 * Дропдаун с поиском: кнопка-триггер + всплывающий список с фильтрацией.
 * Внешний вид соответствует дизайну вкладок (Inter Tight, светлая палитра).
 */
const SearchableSelect = ({
  options,
  value,
  onChange,
  getOptionKey,
  getOptionLabel,
  getOptionPrimary,
  getOptionSecondary,
  getSearchText,
  renderOptionRight,
  placeholder,
  searchPlaceholder,
  emptyText,
  disabled,
}) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const primary = getOptionPrimary || getOptionLabel;
  const buildSearch =
    getSearchText ||
    ((o) => `${primary(o) || ""} ${(getOptionSecondary && getOptionSecondary(o)) || ""}`);

  const q = query.trim().toLowerCase();
  const pool = options.filter((o) => !q || buildSearch(o).toLowerCase().includes(q));

  const close = () => {
    setOpen(false);
    setQuery("");
  };

  const handlePick = (opt) => {
    onChange(opt);
    close();
  };

  return (
    <Box className={classes.root}>
      <button
        type="button"
        disabled={disabled}
        className={classes.trigger}
        onClick={() => setOpen((o) => !o)}
      >
        <span className={value ? classes.triggerLabel : classes.triggerPlaceholder}>
          {value ? getOptionLabel(value) : placeholder}
        </span>
        <Chevron className={classes.chevron} />
      </button>

      {open && (
        <>
          <div className={classes.overlay} onClick={close} />
          <div className={classes.menu}>
            <div className={classes.searchWrap}>
              <SearchIcon className={classes.searchIcon} />
              <input
                autoFocus
                className={classes.search}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
              />
            </div>
            <div className={classes.list}>
              {pool.length === 0 ? (
                <div className={classes.empty}>{emptyText}</div>
              ) : (
                pool.map((o) => {
                  const secondary = getOptionSecondary && getOptionSecondary(o);
                  return (
                    <button
                      type="button"
                      key={getOptionKey(o)}
                      className={classes.option}
                      onClick={() => handlePick(o)}
                    >
                      <div className={classes.optionText}>
                        <div className={classes.optionPrimary}>{primary(o)}</div>
                        {secondary ? (
                          <div className={classes.optionSecondary}>{secondary}</div>
                        ) : null}
                      </div>
                      {renderOptionRight && renderOptionRight(o)}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </Box>
  );
};

SearchableSelect.propTypes = {
  options: PropTypes.array.isRequired,
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  getOptionKey: PropTypes.func,
  getOptionLabel: PropTypes.func.isRequired,
  getOptionPrimary: PropTypes.func,
  getOptionSecondary: PropTypes.func,
  getSearchText: PropTypes.func,
  renderOptionRight: PropTypes.func,
  placeholder: PropTypes.string,
  searchPlaceholder: PropTypes.string,
  emptyText: PropTypes.string,
  disabled: PropTypes.bool,
};

SearchableSelect.defaultProps = {
  value: null,
  getOptionKey: (o) => o.id,
  placeholder: "Выберите…",
  searchPlaceholder: "Поиск…",
  emptyText: "Ничего не найдено",
  disabled: false,
};

export default SearchableSelect;
